/**
 * SINGLE-SCAN-MORNINGS (C) — the login-scan UID handoff.
 *
 * The tag UID a driver scans at the LOGIN gate (nfc_login_required) is the
 * same physical scan the punch model wants for CLOCK IN. Rather than prompt a
 * second scan on the punch screen, the login surface arms this one-shot
 * handle and DriverApp hands it to PunchScreen, which consumes it for the IN
 * direction ONLY — CLOCK OUT always scans (PM model point 4).
 *
 * Plain ref object, not React state: the handoff is set by an async login
 * handler immediately before onLoggedIn and read at most once by the punch
 * screen. It must never persist past the session — DriverApp clears it on
 * kickout and logout (stale-UID-across-sessions is a wrong-device risk).
 * Same no-DOM-harness reasoning as launchGatePunch (lib/punch.ts).
 */

export interface PunchHandoff {
  current: string | null;
}

/** Creates a fresh, empty handoff handle (one per driver session). */
export function makeHandoffRef(): PunchHandoff {
  return { current: null };
}

/** Stores the login-scan UID. A non-string/empty uid arms nothing. */
export function armHandoff(ref: PunchHandoff, uid: string): void {
  ref.current = typeof uid === "string" && uid.length > 0 ? uid : null;
}

/**
 * Reads and CLEARS the handoff — the UID is consumed exactly once. A second
 * consume returns null (one-scan-per-session scope).
 */
export function consumeHandoff(ref: PunchHandoff): string | null {
  const uid = ref.current;
  ref.current = null;
  return uid;
}

/** Drops an unconsumed handoff (kickout / logout / any phase exit). */
export function clearHandoff(ref: PunchHandoff): void {
  ref.current = null;
}
